import { useEffect, useState } from "react";
import { api, type OrderDto, type OrderPartDto, type OrderType, type PartStatus } from "../api";
import { money, monogram, Spinner, tintFor } from "../ui";

/**
 * Страница текущего заказа (п. 10, 21 ТЗ).
 *
 * По ТЗ это главный и единственный источник информации для гостя: SMS и push
 * в первой версии не используются. Значит, страница обязана обновляться сама,
 * без просьбы перезагрузить.
 *
 * Общей полосы «заказ готов на 60%» здесь нет намеренно. Части выполняются
 * независимо, и одно число усреднило бы три кухни в цифру, которая не значит
 * ничего: кофе уже несут, а бургер ещё не начали.
 */

const POLL_MS = 5000;

/** Шаги пути и подписи для гостя. Разные у доставки и самовывоза (п. 10 ТЗ). */
const DELIVERY_STEPS: { status: PartStatus; label: string }[] = [
  { status: "new", label: "Передали заведению" },
  { status: "accepted", label: "Приняли" },
  { status: "cooking", label: "Готовят" },
  { status: "ready", label: "Готово" },
  { status: "delivering", label: "Несут к вам" },
  { status: "delivered", label: "Доставлено" }
];

const PICKUP_STEPS: { status: PartStatus; label: string }[] = [
  { status: "new", label: "Передали заведению" },
  { status: "accepted", label: "Приняли" },
  { status: "cooking", label: "Готовят" },
  { status: "ready", label: "Можно забирать" },
  { status: "handed", label: "Выдано" }
];

export function Order({ token, onLeave }: { token: string; onLeave: () => void }) {
  const [order, setOrder] = useState<OrderDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    let alive = true;

    const load = () =>
      api
        .order(token)
        .then(next => alive && setOrder(next))
        .catch((problem: Error) => alive && setError(problem.message));

    void load();

    /*
     * Опрос, а не поток событий.
     *
     * Поток был бы экономнее, но он требует живого соединения, а телефон в
     * парке уходит в сон, теряет сеть и переключается между вышками. Раз в
     * пять секунд — это дёшево и не ломается от того, что экран погас.
     */
    const timer = window.setInterval(load, POLL_MS);

    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [token]);

  if (error) {
    return (
      <div className="center">
        <p className="notice">{error}</p>
        <button type="button" className="btn ghost" onClick={onLeave}>
          В меню парка
        </button>
      </div>
    );
  }

  if (!order) return <Spinner text="Смотрим, как дела" />;

  const paid = order.paymentStatus === "paid";
  const allDone = order.status === "completed";
  const allCancelled = order.status === "cancelled";

  return (
    <div className="page">
      <div className="hero">
        <p className="order-number">{order.number}</p>
        <h1>
          {allCancelled
            ? "Заказ отменён"
            : allDone
              ? "Всё готово"
              : paid
                ? order.type === "delivery"
                  ? "Заказ везут"
                  : "Заберите в заведении"
                : "Ждём оплату"}
        </h1>
        <p>
          {order.type === "delivery"
            ? "Курьер привезёт заказ по вашему адресу."
            : `Заказ готовят в заведении, ${order.city.name}.`}
        </p>
      </div>

      <section>
        <div className="section-head">
          <h2>{order.parts.length > 1 ? "Части заказа" : "Заказ"}</h2>
        </div>

        <div style={{ marginTop: 12 }}>
          {order.parts.map(part => (
            <Part key={part.number} part={part} type={order.type} />
          ))}
        </div>
      </section>

      <div className="total">
        <span className="muted">{paid ? "Оплачено" : "К оплате"}</span>
        <strong>{money(order.total)}</strong>
      </div>

      {/*
        Неоплаченный заказ можно оплатить отсюда.

        Гость закрывает платёжную страницу чаще, чем кажется: не тот банк,
        нет связи, передумал у окна. Без этой кнопки его заказ существует, но
        сделать с ним нельзя ничего — остаётся оформлять заново.
      */}
      {!paid && !allCancelled ? (
        <button
          type="button"
          className="btn"
          disabled={paying}
          onClick={() => {
            setPaying(true);
            api
              .startPayment(token, api.newKey())
              .then(payment => {
                if (payment.confirmationUrl) window.location.href = payment.confirmationUrl;
              })
              .catch((problem: Error) => setError(problem.message))
              .finally(() => setPaying(false));
          }}
        >
          {paying ? "Открываем оплату…" : "Оплатить"}
        </button>
      ) : null}

      <button type="button" className="btn ghost" onClick={onLeave}>
        Вернуться в парк
      </button>
    </div>
  );
}

function Part({ part, type }: { part: OrderPartDto; type: OrderType }) {
  const steps = type === "pickup" ? PICKUP_STEPS : DELIVERY_STEPS;
  const cancelled = part.status === "cancelled";
  /*
   * До оплаты путь не начат: ни один шаг не горит.
   *
   * Заведение о заказе ещё не знает — части раздаются точкам в момент оплаты
   * (п. 11 ТЗ). «Передали заведению» на неоплаченном заказе обещало бы гостю
   * то, чего не произошло.
   */
  const waiting = part.status === "awaiting_payment";
  const index = steps.findIndex(step => step.status === part.status);
  const done = index === steps.length - 1;

  return (
    <div className={`part${done ? " done" : ""}${cancelled ? " cancelled" : ""}`}>
      <div className="part-head">
        <span className="chip" style={{ background: tintFor(part.point.name) }}>
          {monogram(part.point.name)}
        </span>
        <strong>{part.point.name}</strong>
        <span className="muted small">{money(part.total)}</span>
      </div>

      {cancelled ? (
        <>
          <p className="part-status">Отменено</p>
          <p className="part-eta">
            {part.cancelReason ?? "Деньги за эту часть вернутся на карту."}
          </p>
        </>
      ) : (
        <>
          <div className="track">
            {steps.map((step, position) => (
              <i
                key={step.status}
                className={
                  waiting
                    ? undefined
                    : position < index
                      ? "on"
                      : position === index
                        ? "now"
                        : undefined
                }
              />
            ))}
          </div>

          <p className="part-status">
            {waiting ? "Ждём оплату" : (steps[Math.max(index, 0)]?.label ?? "В работе")}
          </p>

          {part.etaMinutes && !done ? (
            <p className="part-eta">Обещают примерно за {part.etaMinutes} мин</p>
          ) : null}
        </>
      )}

      <div className="part-lines">
        {part.lines.map((line, position) => (
          <div key={`${line.title}-${position}`} className="part-line">
            <span>
              {line.title}
              {line.qty > 1 ? ` × ${line.qty}` : ""}
            </span>
            <span>{money(line.total)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
